import { css } from '@emotion/react'
import styled from '@emotion/styled'
import { Button } from 'app/components/Common/Button'
import { Props as MenuItemProps } from 'app/components/Common/Header/MenuItem'
import { ModalIBE } from 'app/components/Common/Header/ModalIBE'
import { Navigation } from 'app/components/Common/Header/Navigation'
import { Props as ImageProps } from 'app/components/Common/Image'
import { FlashNews, Props as FlashNewsProps } from 'app/components/FlashNews'
import { HeaderContext } from 'app/containers/Header'
import { theme } from 'app/theme'
import { Media, MediaContextProvider, mediaStyle } from 'app/theme/media'
import useLockedBody from 'app/utils/useLockedBody'
import useMediaQuery from 'app/utils/useMediaQuery'
import { useVocabularyData } from 'app/utils/vocabulary'
import { Link } from 'gatsby'
import { gsap } from 'gsap'
import React, { memo, useContext, useEffect, useRef, useState } from 'react'
import { isMobile } from 'react-device-detect'

import { Hamburger } from './Hamburger'
import { HamburgerClose } from './HamburgerClose'
import { Props as LanguageItemProps } from './LanguageItem'
import { Languages } from './Languages'
import { LanguagesMobile } from './LanguagesMobile'

export interface HeaderMenuItemProps {
  id?: string
  languageCode?: string
  languagePrefix?: string | null
  title: string
  url: string
}

export interface Props {
  flashNewsProps?: FlashNewsProps
  headerMenuItems: HeaderMenuItemProps[]
  languagesList: LanguageItemProps[]
  menuItems: MenuItemProps[]
  pageID?: string
  bookLabel?: string
  bookTitle?: string
  bookDescription?: string
  bookImage?: ImageProps
  benefitsTitle?: string
  benefitsList?: any[]
  phone?: string
}

export const Header = memo(function Header({
  flashNewsProps,
  headerMenuItems,
  languagesList,
  menuItems,
  bookLabel,
  bookTitle,
  bookDescription,
  bookImage,
  benefitsTitle,
  benefitsList,
}: Props) {
  const { languageCode, languagePrefix, phone, syncroStatus, variant } =
    useContext(HeaderContext) as any

  const [_locked, setLocked] = useLockedBody()
  const [modalStatus, setModalStatus] = useState(false)
  const [scroll, setScroll] = useState(false)
  const [modalIBEStatus, setModalIBEStatus] = useState(false)

  const mainHeader = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const onScroll = () => {
      setScroll(window.scrollY > 120)
    }

    window.addEventListener('scroll', onScroll)
    return () => {
      window.removeEventListener('scroll', onScroll)
    }
  }, [])

  const isDesktop = useMediaQuery('(min-width: 992px)')

  useEffect(() => {
    if (isDesktop) {
      let ctx = gsap.context(() => {
        gsap.to('.intro-fade-up', {
          opacity: 1,
          y: 0,
          stagger: 0.15,
          ease: 'circ.out',
          duration: 1,
          delay: 0.5,
        })
      }, mainHeader)

      return () => ctx.revert()
    }
  }, [isDesktop])

  return (
    <Container>
      <style type="text/css">{mediaStyle}</style>
      <MediaContextProvider>
        <Head
          ref={mainHeader}
          variant={scroll || variant === 'fixed' ? 'fixed' : 'default'}
        >
          {flashNewsProps ? <FlashNews {...flashNewsProps} /> : null}
          <Wrapper>
            <Left className="intro-fade-up">
              <Hamburger
                languageCode={languageCode}
                className={scroll || variant === 'fixed' ? 'fixed' : ''}
                onClick={() => {
                  if (!modalStatus) {
                    setLocked(true)
                    setModalStatus(true)
                  } else {
                    setLocked(false)
                    setModalStatus(false)
                  }
                }}
              />
            </Left>

            <Logo to={languagePrefix ? `/${languagePrefix}` : '/'}>
              <div className="intro-fade-up">
                {scroll || variant === 'fixed' ? (
                  <img src="/logo_header_fixed.png" alt="Hotel Corallo" />
                ) : (
                  <img src="/logo_header.png" alt="Hotel Corallo" />
                )}
              </div>
            </Logo>

            <Media lessThan="tabletMedium">
              <HeaderMobile
                className={scroll || variant === 'fixed' ? 'fixed' : ''}
              >
                <LogoMobile to={languagePrefix ? `/${languagePrefix}` : '/'}>
                  <img
                    className="intro-fade-up"
                    src="/logo_header_fixed.png"
                    alt="Hotel Corallo"
                  />
                </LogoMobile>
              </HeaderMobile>
            </Media>

            <Right>
              <Media greaterThanOrEqual="tabletMedium">
                <Languages
                  isSticky={scroll || variant === 'fixed' ? true : false}
                  languagesList={languagesList}
                  languagePrefix={languagePrefix || null}
                />
              </Media>
              <Media lessThan="tabletMedium">
                <MobileButton
                  href={useVocabularyData('booking-link', languageCode)}
                  target="_blank"
                >
                  <img src="/icon_calendar.svg" />
                  <span>{useVocabularyData('book', languageCode)}</span>
                </MobileButton>
              </Media>
              <Media greaterThanOrEqual="tabletMedium">
                <Button
                  URL="#"
                  label={useVocabularyData('book-now', languageCode)}
                  className="intro-fade-up"
                  variant={
                    scroll || variant === 'fixed'
                      ? 'default'
                      : 'default-no-bg-hover'
                  }
                  onClick={() => {
                    if (!modalIBEStatus) {
                      setLocked(true)
                      setModalIBEStatus(true)
                    }
                  }}
                />
              </Media>
            </Right>
          </Wrapper>
        </Head>

        <SideHead>
          <a
            href="https://www.simplebooking.it/ibe/minishop?hid=350"
            onClick={() => {
              typeof window.gtag !== 'undefined' &&
                window.gtag('event', 'Click', {
                  event_category: 'Website',
                  event_label: 'Shop',
                })
            }}
            target="_blank"
          >
            <SideHeadeEl>
              <img src="/ico_shop.svg" />
              <span>SHOP</span>
            </SideHeadeEl>
          </a>
          <a
            href={`tel:${phone}`}
            onClick={() => {
              if (isMobile) {
                typeof window.gtag !== 'undefined' &&
                  window.gtag('event', 'Click', {
                    event_category: 'Website',
                    event_label: 'Phone Number',
                  })
              }
            }}
            target="_blank"
          >
            <SideHeadeEl>
              <img src="/ico_phone.svg" />
              <span>PHONE</span>
            </SideHeadeEl>
          </a>
          <Link to={headerMenuItems[0]?.url}>
            <SideHeadeEl>
              <img src="/ico_pencil.svg" />
              <span>{headerMenuItems[0]?.title}</span>
            </SideHeadeEl>
          </Link>
          <Link to={headerMenuItems[1]?.url}>
            <SideHeadeEl>
              <img src="/ico_question.svg" />
              <span>{headerMenuItems[1]?.title}</span>
            </SideHeadeEl>
          </Link>
        </SideHead>

        <Modal className={modalStatus ? 'open' : undefined}>
          <Navigation menuItems={menuItems} modalStatus={modalStatus} />
          <CoverText />
          <HamburgerClose
            languageCode={languageCode}
            className={`fixed-bar--${scroll}`}
            onClick={() => {
              if (!modalStatus) {
                setLocked(true)
                setModalStatus(true)
              } else {
                setLocked(false)
                setModalStatus(false)
              }
            }}
          />
          <Media lessThan="tabletMedium">
            <LanguagesMobile
              scroll={scroll}
              languagesList={languagesList}
              languagePrefix={languagePrefix || null}
            />
          </Media>

          <Media lessThan="tabletMedium">
            <MobileButton
              href={useVocabularyData('booking-link', languageCode)}
              target="_blank"
            >
              <img src="/icon_calendar.svg" />
              <span>{useVocabularyData('book', languageCode)}</span>
            </MobileButton>
          </Media>
          <Media greaterThanOrEqual="tabletMedium">
            <NavigationBookingButton
              variant="white-outline"
              URL="#"
              label={useVocabularyData('book-now', languageCode)}
              className={`fixed-bar--${scroll}`}
              onClick={() => {
                if (!modalIBEStatus) {
                  setLocked(true)
                  setModalIBEStatus(true)
                }
              }}
            />
          </Media>
        </Modal>

        <ModalIBEWrapper className={modalIBEStatus ? 'open' : undefined}>
          <ModalIBE
            languageCode={languageCode}
            languagePrefix={languagePrefix || 'en'}
            modalStatus={modalIBEStatus}
            syncroStatus={syncroStatus}
            bookLabel={bookLabel}
            bookTitle={bookTitle}
            bookDescription={bookDescription}
            bookImage={bookImage}
            benefitsTitle={benefitsTitle}
            benefitsList={benefitsList}
          />
          <HamburgerClose
            languageCode={languageCode}
            onClick={() => {
              setLocked(false)
              setModalIBEStatus(false)
            }}
            className="modalIBE--close"
          />
        </ModalIBEWrapper>
      </MediaContextProvider>
    </Container>
  )
})

const Container = styled.section``

const Head = styled.div<HeadProps>`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: auto;
  z-index: 99;

  .intro-fade-up {
    opacity: 0;
    transform: translateY(40px);
  }

  @media (max-width: 991px) {
    position: fixed;
    height: 4.375rem;

    .intro-fade-up {
      opacity: 1;
      transform: none;
    }
  }

  ${({ variant }) => {
    switch (variant) {
      case 'fixed':
        return css`
          top: 0;
          position: fixed;
          height: 4.5rem;
          background-color: ${theme.colors.variants.neutralLight2};
          box-shadow: 0px 2px 16px rgb(0 0 0 / 0.02);
          animation: fadeInTop 0.3s;

          > div {
            margin: 0 auto;
          }

          .item-flashnews {
            position: absolute;
            transform: translateY(-100%);
            display: none;
          }

          @media (max-width: 768px) {
            height: 4.375rem;
            background-color: transparent;
            box-shadow: none;
            animation: none;
          }
        `
    }
  }}

  @keyframes fadeInTop {
    0% {
      top: -10rem;
    }
    100% {
      top: 0;
    }
  }

  @media (max-width: 991px) {
    top: 0;
  }
`

const SideHead = styled.aside`
  position: fixed;
  bottom: 0;
  right: 0;
  width: 72px;
  z-index: 10;

  @media (max-width: 991px) {
    width: 100%;
    display: flex;

    a {
      display: block;
      width: 25%;
      position: relative;

      &:after {
        content: ' ';
        display: block;
        width: 1px;
        height: 40%;
        right: 0;
        opacity: 0.3;
        top: 50%;
        transform: translate3d(0, -50%, 0);
        position: absolute;
        background-color: ${({ theme }) => theme.colors.variants.neutralLight1};
      }

      &:last-child::after {
        display: none;
      }
    }
  }
`

const SideHeadeEl = styled.div`
  padding: 20px 5px;
  font-size: 11px;
  text-align: center;
  color: ${({ theme }) => theme.colors.variants.neutralLight1};
  font-family: ${({ theme }) => theme.fontFamily.subheading};
  background: ${theme.colors.variants.primaryDark};

  img {
    display: block;
    margin: 0 auto;
    margin-bottom: 8px;
    transition: all 200ms linear;
  }

  span {
    text-transform: uppercase;
  }

  &:hover img {
    opacity: 0.4;
  }

  @media (max-width: 991px) {
    margin: 0;
    padding: 15px 5px;
  }
`

const Wrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin: 1.5rem auto 0;
  height: 100%;
  position: relative;
`

const Left = styled.div`
  display: flex;
  align-items: center;
  padding-left: 2.25rem;

  @media (max-width: 991px) {
    padding: 0;
  }
`

const Right = styled.div`
  display: flex;
  align-items: center;
  padding-right: 2.25rem;

  @media (max-width: 991px) {
    padding-right: 10px;
  }
`

const Logo = styled(Link)`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);

  img {
    width: 138px;
    height: auto;
  }

  @media (max-width: 991px) {
    display: none;
  }
`

const Modal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  opacity: 0;
  visibility: hidden;
  transition: 0.2s ease-out;
  pointer-events: none;
  z-index: 99;
  background-color: ${theme.colors.variants.primaryDark};

  &.open {
    opacity: 1;
    visibility: visible;
    pointer-events: auto;
  }
`
const NavigationBookingButton = styled(Button)`
  position: fixed;
  top: 2.25rem;
  right: 2.25rem;

  &.fixed-bar--true {
    top: 2.25rem;
  }

  @media (max-width: 991px) {
    top: 2.2rem !important;
    right: 10px;
  }
`

const ModalIBEWrapper = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  transition: 0.2s ease-out;
  pointer-events: none;
  z-index: 999;
  overflow: auto;
  background-color: ${theme.colors.variants.neutralLight1};
  transform: translate3d(0, -100%, 0);
  transition: transform 1s cubic-bezier(0.785, 0.135, 0.15, 0.86);

  &.open {
    pointer-events: auto;
    transform: translate3d(0, 0%, 0);
  }
`

const CoverText = styled.div`
  display: none;

  @media (max-width: 991px) {
    width: 100%;
    height: 20vh;
    position: absolute;
    left: 0;
    top: 0;
    display: block;
    background: ${theme.colors.variants.primaryDark};
    background: linear-gradient(
      0deg,
      rgba(0, 0, 0, 0) 0%,
      rgba(93, 127, 160, 1) 50%
    );
  }
`

const HeaderMobile = styled.div`
  height: 70px;
  width: calc(100% - 140px);
  left: 70px;
  top: 0;
  position: fixed;
  transition: all 200ms linear;

  img {
    filter: brightness(1000%);
  }

  &.fixed {
    background-color: ${theme.colors.variants.neutralLight2};

    img {
      filter: brightness(50%);
    }
  }
`
const LogoMobile = styled(Link)`
  position: absolute;
  top: 35px;
  left: 50%;
  transform: translate3d(-50%, -50%, 0);

  img {
    width: 140px;
    height: auto;
  }
`
const MobileButton = styled.a`
  position: fixed;
  top: 0;
  right: 0;
  width: 70px;
  height: 70px;
  display: block;
  text-align: center;
  padding-top: 16px;
  font-size: 11px;
  background: ${theme.colors.variants.primaryLight};
  color: ${theme.colors.variants.neutralLight1};
  text-transform: uppercase;
  font-family: ${({ theme }) => theme.fontFamily.subheading};

  img {
    width: 14px;
    display: block;
    margin: 0 auto;
  }
  span {
    display: block;
    margin-top: 8px;
  }
`

interface HeadProps {
  variant: Variant
}

export type SyncroStatus = 'default' | 'open'

export type Variant = 'default' | 'compact' | 'fixed'
